import React, { useState, useEffect, useCallback } from 'react';
import { FileText, AlertTriangle, Info, AlertCircle, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function AdminLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [severity, setSeverity] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit), severity });
      const data = await apiGet(`${API_ROUTES.admin.logs}?${params}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [page, severity]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const severityIcon = (s) => {
    if (s === 'error') return <AlertCircle size={12} className="text-red-400" />;
    if (s === 'warning') return <AlertTriangle size={12} className="text-amber-400" />;
    return <Info size={12} className="text-blue-400" />;
  };

  const severityColor = (s) => {
    if (s === 'error') return 'bg-red-500/20 text-red-400';
    if (s === 'warning') return 'bg-amber-500/20 text-amber-400';
    return 'bg-blue-500/20 text-blue-400';
  };

  const exportVisibleLogs = () => {
    const header = ['created_at', 'severity', 'category', 'message', 'details'];
    const rows = logs.map((log) => header.map((key) => `"${String(log[key] || '').replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[header.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-logs-page-${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-100">System Logs</h1>
          <p className="text-sm text-gray-500 mt-1">{total} events recorded</p>
        </div>
        <div className="flex items-center gap-2">
          {['', 'info', 'warning', 'error'].map((s) => (
            <button
              key={s}
              onClick={() => { setSeverity(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                severity === s ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-dark-card text-gray-500 border border-dark-border hover:text-gray-300'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Logs */}
      <div className="glass-card bg-dark-card overflow-hidden">
        <div className="divide-y divide-dark-border/50">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No logs found</div>
          ) : (
            logs.map((log, i) => (
              <div key={log.id || i} className="px-4 py-3 hover:bg-white/[0.02] transition-colors">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">{severityIcon(log.severity)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${severityColor(log.severity)}`}>
                        {log.severity}
                      </span>
                      <span className="text-[10px] text-gray-600 bg-dark-bg px-1.5 py-0.5 rounded">{log.category}</span>
                      <span className="text-[10px] text-gray-600 ml-auto">{log.created_at?.replace('T', ' ').slice(0, 19)}</span>
                    </div>
                    <p className="text-sm text-gray-300">{log.message}</p>
                    {log.details && <p className="text-xs text-gray-500 mt-0.5">{log.details}</p>}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-dark-border">
            <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-md border border-dark-border text-gray-400 hover:text-gray-200 disabled:opacity-30">
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-md border border-dark-border text-gray-400 hover:text-gray-200 disabled:opacity-30">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
